import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowLeft, Handshake, School, Microscope, Heart, Mail, Phone, Globe, Send } from 'lucide-react';
import HeroSlider from '../components/shared/HeroSlider';
import Footer from '../components/shared/Footer';

export default function Partners() {
  const { t, i18n } = useTranslation('partners');
  const isRTL = i18n.language === 'ar';

  // Hero slides data
  const heroSlides = [
    {
      title: t('hero.title'),
      highlight: t('hero.titleHighlight'),
      description: t('hero.description'),
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
    {
      title: t('hero.title'),
      highlight: t('hero.titleHighlight'),
      description: t('hero.description'),
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    },
  ];

  return (
    <div className={`min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* Hero Slider - Same as Homepage */}
      <HeroSlider slides={heroSlides} currentPage="partners" />

      {/* Collaboration Opportunities */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('hero.badge')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">{t('opportunities.title', 'Collaboration Opportunities')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Card 1: Institutions */}
                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                                <School className="w-6 h-6" />
                            </div>
                        </div>
                        <CardTitle className="text-xl">{t('opportunities.institutions.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600">
                            {t('opportunities.institutions.description')}
                        </p>
                    </CardContent>
                </Card>

                {/* Card 2: Researchers */}
                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 text-purple-600">
                                <Microscope className="w-6 h-6" />
                            </div>
                        </div>
                        <CardTitle className="text-xl">{t('opportunities.researchers.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600">
                            {t('opportunities.researchers.description')}
                        </p>
                    </CardContent>
                </Card>

                {/* Card 3: Teachers */}
                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 text-green-600">
                                <Handshake className="w-6 h-6" />
                            </div>
                        </div>
                        <CardTitle className="text-xl">{t('opportunities.teachers.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600">
                            {t('opportunities.teachers.description')}
                        </p>
                    </CardContent>
                </Card>

                {/* Card 4: Supporters */}
                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <CardHeader>
                        <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 text-red-600">
                                <Heart className="w-6 h-6" />
                            </div>
                        </div>
                        <CardTitle className="text-xl">{t('opportunities.supporters.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600">
                            {t('opportunities.supporters.description')}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center space-y-12">
            <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('contact.title')}</h2>
                <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                    {t('contact.description')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Email 1 */}
                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center gap-4 hover:bg-slate-750 transition-colors">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-center">
                        <p className="text-slate-400 text-sm uppercase tracking-wider font-semibold mb-1">{t('contact.academicEmail')}</p>
                        <a href="mailto:ybadarin@staff.alquds.edu" className="text-white font-medium hover:text-blue-400 transition-colors" dir="ltr">
                            ybadarin@staff.alquds.edu
                        </a>
                    </div>
                </div>

                {/* Email 2 */}
                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center gap-4 hover:bg-slate-750 transition-colors">
                    <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center">
                        <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-center">
                        <p className="text-slate-400 text-sm uppercase tracking-wider font-semibold mb-1">{t('contact.internationalEmail')}</p>
                        <a href="mailto:yazidbaj@uef.fi" className="text-white font-medium hover:text-indigo-400 transition-colors" dir="ltr">
                            yazidbaj@uef.fi
                        </a>
                    </div>
                </div>

                {/* WhatsApp */}
                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center gap-4 hover:bg-slate-750 transition-colors">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                        <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-center">
                        <p className="text-slate-400 text-sm uppercase tracking-wider font-semibold mb-1">{t('contact.whatsapp')}</p>
                        <a href="https://wa.me/970599292050" target="_blank" rel="noopener noreferrer" className="text-white font-medium hover:text-green-400 transition-colors" dir="ltr">
                            +970-599292050
                        </a>
                    </div>
                </div>
            </div>

            <div className="pt-8">
                <Button size="lg" className="h-14 px-10 bg-white text-slate-900 hover:bg-blue-50 font-semibold rounded-full" asChild>
                    <a href="mailto:ybadarin@staff.alquds.edu">
                        <Send className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('contact.sendMessage')}
                    </a>
                </Button>
            </div>
        </div>
      </section>

      {/* Footer - Same as Homepage */}
      <Footer />
    </div>
  );
}
